import Footer from "@/components/layout/Footer";
import Header from "@/components/layout/Header";
import { scrollToTop } from "@/utils";
import {
  Cookie,
  Database,
  ExternalLink,
  Eye,
  FileCheck,
  Lock,
  Mail,
  MapPin,
  Phone,
  Shield,
  UserCheck,
} from "lucide-react";
import { useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";

const PagePrivacyPolicy = () => {
  useEffect(() => {
    scrollToTop();
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 font-sans flex flex-col justify-between">
      <Helmet>
        <title>Politique de Confidentialité | Église de Dieu Garizim</title>
        <meta
          name="description"
          content="Politique de confidentialité officielle de l'Église de Dieu Garizim (Montréal & Rive-Sud). Informations relatives à la protection des données, aux cookies et à la conformité Google AdSense."
        />
        <link rel="canonical" href="https://egarizim.com/politique-de-confidentialite" />
      </Helmet>

      <Header />

      <main className="pt-24 pb-16">
        {/* Hero Header */}
        <section className="bg-gradient-to-b from-slate-900 to-slate-800 text-white py-12 sm:py-16 px-4">
          <div className="container mx-auto max-w-4xl text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/15 text-amber-300 text-xs font-bold uppercase tracking-wider mb-4 backdrop-blur-sm">
              <Shield className="w-4 h-4" />
              <span>Transparence & Protection Légale</span>
            </div>
            <h1 className="text-3xl sm:text-5xl font-black tracking-tight mb-4">
              Politique de Confidentialité
            </h1>
            <p className="text-slate-300 text-sm sm:text-base max-w-2xl mx-auto leading-relaxed">
              Dernière mise à jour : 1er Février 2026. L'Église de Dieu Garizim
              s'engage à protéger la vie privée de ses membres, visiteurs et
              utilisateurs de son site internet.
            </p>
          </div>
        </section>

        {/* Content Container */}
        <section className="container mx-auto max-w-4xl px-4 sm:px-6 -mt-6">
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200/80 p-6 sm:p-10 space-y-10 text-slate-700 leading-relaxed text-sm sm:text-base">
            {/* Introduction */}
            <div className="space-y-4">
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2.5 pb-2 border-b border-slate-100">
                <FileCheck className="w-5 h-5 text-church-blue-600" />
                1. Introduction et Champ d'Application
              </h2>
              <p>
                La présente Politique de Confidentialité décrit la manière dont
                l'<strong>Église de Dieu Garizim</strong> (ci-après désignée «
                l'Église », « nous », « notre » ou « nos »), organisme religieux
                à but non lucratif établi au Québec (Canada), recueille, utilise,
                conserve et protège les renseignements personnels des
                utilisateurs qui naviguent sur la plateforme web{" "}
                <strong>egarizim.com</strong>.
              </p>
              <p>
                Cette politique est rédigée en conformité avec les exigences de
                la législation québécoise (notamment la{" "}
                <em>
                  Loi sur la protection des renseignements personnels dans le
                  secteur privé — Loi 25
                </em>
                ), les lois fédérales canadiennes (<em>LPRPDE / PIPEDA</em>)
                ainsi que les règles de transparence imposées par les réseaux de
                diffusion partenaires, dont{" "}
                <strong>Google AdSense</strong>.
              </p>
            </div>

            {/* Google AdSense & Cookies Disclosures (Critical requirement) */}
            <div className="space-y-4 p-5 sm:p-6 bg-amber-50/70 border-l-4 border-amber-500 rounded-r-xl">
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2.5">
                <Cookie className="w-5 h-5 text-amber-600" />
                2. Cookies Tiers & Publicité Google AdSense
              </h2>
              <p className="text-slate-800 font-medium">
                Notre site internet peut afficher des annonces publicitaires
                fournies par le programme <strong>Google AdSense</strong>. Afin de
                respecter scrupuleusement les exigences de confidentialité de
                Google et les standards internationaux de transparence publicitaire, nous vous informons expressément des éléments suivants :
              </p>
              <ul className="list-disc pl-5 space-y-2 text-slate-700">
                <li>
                  Des fournisseurs tiers, y compris <strong>Google</strong>,
                  utilisent des <em>cookies</em> pour diffuser des annonces
                  pertinentes basées sur les visites antérieures d'un internaute
                  sur notre site web ou sur d'autres sites internet.
                </li>
                <li>
                  Grâce aux cookies publicitaires (dont les témoins DoubleClick / DART), Google et ses partenaires peuvent adapter les annonces
                  diffusées auprès des internautes en fonction de leur historique de navigation
                  sur nos pages et/ou d'autres sites du réseau Internet.
                </li>
                <li>
                  <strong>Lien direct de désactivation Google :</strong> Les utilisateurs
                  peuvent à tout moment choisir de désactiver la personnalisation des annonces
                  en accédant directement aux paramètres des annonces Google via ce lien :{" "}
                  <a
                    href="https://adssettings.google.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-church-blue-700 font-semibold underline inline-flex items-center gap-1"
                  >
                    https://adssettings.google.com/{" "}
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                  .
                </li>
                <li>
                  <strong>Plateformes de désactivation tierces (Opt-Out) :</strong> Vous pouvez également désactiver globalement les cookies de fournisseurs
                  tiers pour la publicité ciblée par centres d'intérêt en consultant les plateformes de désactivation agréées :
                  <div className="flex flex-wrap gap-2 pt-1">
                    <a
                      href="https://youradchoices.ca/fr/en-savoir-plus/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-church-blue-700 font-semibold underline inline-flex items-center gap-1"
                    >
                      YourAdChoices Canada (youradchoices.ca){" "}
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                    <span>•</span>
                    <a
                      href="https://www.aboutads.info/choices/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-church-blue-700 font-semibold underline inline-flex items-center gap-1"
                    >
                      Digital Advertising Alliance (aboutads.info/choices){" "}
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                    <span>•</span>
                    <a
                      href="https://optout.networkadvertising.org/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-church-blue-700 font-semibold underline inline-flex items-center gap-1"
                    >
                      Network Advertising Initiative (NAI){" "}
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </div>
                </li>
                <li>
                  <strong>Conformité Loi 25 (Québec) & RGPD (Europe) — Recueil préalable du consentement :</strong> Conformément à la Loi 25 du Québec et au Règlement Général sur la Protection des Données (RGPD), aucun cookie publicitaire ou traceur de profilage non essentiel n'est déposé sur votre terminal sans votre consentement préalable, libre et éclairé. Vous conservez le droit de modifier ou de révoquer votre choix à tout moment.
                </li>
              </ul>
            </div>

            {/* Données collectées */}
            <div className="space-y-4">
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2.5 pb-2 border-b border-slate-100">
                <Database className="w-5 h-5 text-church-blue-600" />
                3. Renseignements Personnels Collectés
              </h2>
              <p>
                Nous collectons uniquement les informations nécessaires au bon
                fonctionnement de nos services spirituels et à la communication
                fraternelle :
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                  <h3 className="font-bold text-slate-900 mb-1.5 flex items-center gap-2">
                    <UserCheck className="w-4 h-4 text-emerald-600" />
                    Informations transmises volontairement
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-600">
                    Lorsque vous utilisez notre formulaire de contact ou nous
                    écrivez par courriel : nom, prénom, adresse courriel, numéro
                    de téléphone et contenu de votre message (requête de prière,
                    demande d'information pastorale).
                  </p>
                </div>

                <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                  <h3 className="font-bold text-slate-900 mb-1.5 flex items-center gap-2">
                    <Eye className="w-4 h-4 text-blue-600" />
                    Données de navigation et journaux techniques
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-600">
                    Adresse IP anonymisée, type de navigateur, système
                    d'exploitation, pages consultées (chants, articles, horaires
                    de culte) et horodatage des visites pour des fins
                    statistiques d'amélioration du service.
                  </p>
                </div>
              </div>
            </div>

            {/* Finalité de l'utilisation */}
            <div className="space-y-4">
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2.5 pb-2 border-b border-slate-100">
                <Lock className="w-5 h-5 text-church-blue-600" />
                4. Finalité de l'Utilisation des Données
              </h2>
              <p>Les données collectées sont utilisées exclusivement pour :</p>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>
                  Répondre à vos demandes de contact, d'assistance pastorale ou
                  d'intercession spirituelle.
                </li>
                <li>
                  Assurer la disponibilité, la rapidité et la sécurité
                  technique du site web (recherche des Chants d'Espérance, accès
                  aux articles).
                </li>
                <li>
                  Mesurer l'audience générale du site afin d'optimiser nos
                  contenus éditoriaux et l'expérience utilisateur.
                </li>
                <li>
                  Respecter les obligations légales et réglementaires
                  applicables au Québec et au Canada.
                </li>
              </ul>
              <p className="text-xs sm:text-sm bg-slate-100 p-3 rounded-lg text-slate-600 italic">
                Nous ne vendons, ne louons et ne commercialisons jamais vos
                renseignements personnels à des tiers.
              </p>
            </div>

            {/* Dons et Sécurité Financière */}
            <div className="space-y-4">
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2.5 pb-2 border-b border-slate-100">
                <Shield className="w-5 h-5 text-church-blue-600" />
                5. Dons en Ligne et Transactions Financières
              </h2>
              <p>
                Les dons financiers transmis en ligne sont traités exclusivement
                par des passerelles de paiement externes sécurisées et certifiées
                (notamment <strong>PayPal</strong>). L'Église de Dieu Garizim ne
                collecte, ne stocke et ne conserve aucun numéro de carte de
                crédit ou coordonnée bancaire sur ses propres serveurs.
              </p>
            </div>

            {/* Droits des utilisateurs */}
            <div className="space-y-4">
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2.5 pb-2 border-b border-slate-100">
                <FileCheck className="w-5 h-5 text-church-blue-600" />
                6. Vos Droits (Loi 25 du Québec)
              </h2>
              <p>
                Conformément aux lois québécoises et canadiennes sur la
                protection de la vie privée, vous bénéficiez des droits suivants
                concernant vos données personnelles :
              </p>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>
                  <strong>Droit d'accès :</strong> Obtenir la confirmation que
                  des données vous concernant sont détenues et en recevoir une
                  copie.
                </li>
                <li>
                  <strong>Droit de rectification :</strong> Demander la
                  correction de données inexactes ou incomplètes.
                </li>
                <li>
                  <strong>Droit au retrait du consentement :</strong> Retirer à
                  tout moment votre consentement au traitement de vos données
                  personnelles.
                </li>
                <li>
                  <strong>Droit à l'effacement :</strong> Demander la suppression
                  définitive de vos informations de nos fichiers de contact.
                </li>
              </ul>
            </div>

            {/* Contact / Responsable de la protection */}
            <div className="space-y-4 p-6 bg-slate-900 text-white rounded-2xl">
              <h2 className="text-xl font-bold text-amber-400 flex items-center gap-2">
                <Mail className="w-5 h-5" />
                7. Responsable de la Protection des Renseignements Personnels
              </h2>
              <p className="text-slate-300 text-sm">
                Pour toute question, demande d'accès ou réclamation concernant
                cette politique de confidentialité ou le traitement de vos
                données, vous pouvez contacter notre responsable désigné :
              </p>
              <div className="pt-2 text-sm text-slate-200 space-y-1.5">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-white">Organisme :</span>
                  <span>Église de Dieu de Garizim</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-amber-400 shrink-0" />
                  <span>11800 4e Avenue, Montréal, QC H1E 5Y2, Canada</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-amber-400 shrink-0" />
                  <a
                    href="mailto:egarizim@gmail.com"
                    className="underline hover:text-amber-300"
                  >
                    egarizim@gmail.com
                  </a>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-amber-400 shrink-0" />
                  <span>(514) 322-4921</span>
                </div>
              </div>
            </div>

            {/* Navigation footer */}
            <div className="pt-4 flex flex-wrap items-center justify-between gap-4 border-t border-slate-200 text-xs sm:text-sm text-slate-500">
              <Link
                to="/"
                className="hover:text-church-blue-700 font-medium transition-colors"
              >
                &larr; Retour à l'accueil
              </Link>
              <Link
                to="/conditions-utilisation"
                className="hover:text-church-blue-700 font-medium transition-colors underline"
              >
                Consulter les Conditions Générales d'Utilisation &rarr;
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default PagePrivacyPolicy;
